import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  getListConversationItemsApiV1ConversationItemsGetQueryKey,
  listConversationItemsApiV1ConversationItemsGet,
  useGetConversationApiV1ConversationsConversationIdGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { ListConversationItemsApiV1ConversationItemsGetCursorDir } from '#/shared/api/generated/communication/models'
import type {
  ConversationHeader,
  ConversationItemSchema,
  ConversationMainline,
  CustomerSummary,
  ListConversationItemsApiV1ConversationItemsGetParams,
} from '#/shared/api/generated/communication/models'
import { formatPhone } from '#/shared/lib/format'

import { counterpartyTitle } from '../lib/customer-display'
import { itemsToEntries, userName } from '../lib/map-timeline'
import { isInvalidCursor, useCursorRecovery } from '../lib/use-cursor-recovery'
import type {
  ConversationTarget,
  ThreadMainline,
  TimelineItem,
} from './conversation'
import { useMarkConversationRead } from './use-mark-read'

/** Items fetched per page; older pages load as the user scrolls to the top. */
const PAGE_SIZE = 30

interface ConversationThread {
  /** Resolved counterparty title for the header. */
  name: string
  /** Formatted counterparty number for the header subtitle. */
  phone: string
  /** E.164 counterparty number used to send replies. */
  toNumber: string
  /** Resolved CRM customer id, when the counterparty maps to one. */
  customerId?: string
  /** Resolved counterparty customer, carrying inline SMS deliverability.
   *  `undefined` while the sources are still loading; `null` once it's
   *  confirmed the number maps to no CRM customer (number-health fallback). */
  customer: CustomerSummary | null | undefined
  /** The counterparty opted this line pair out of SMS (replied STOP) — sends
   *  are rejected, so the composer shows the opted-out banner. */
  optedOut: boolean
  /** Shared mainline this thread lives on, when it's a mainline (shared-line)
   *  thread; undefined for personal-line threads. Drives the composer "From"
   *  row — a mainline thread sends from this line and hides the line picker. */
  mainline?: ThreadMainline
  /** Server message/call entries (sorted, no date separators yet). */
  entries: ReadonlyArray<TimelineItem>
  isLoading: boolean
  isError: boolean
  /** The counterparty is blocked org-wide — the composer is replaced by Unblock. */
  blocked: boolean
  /** Favorited counterparty — drives the header menu toggle. */
  starred: boolean
  /** True while older pages remain to load (scroll up). */
  hasMore: boolean
  /** True while an older page is being fetched. */
  isFetchingMore: boolean
  /** Fetch the next (older) page. */
  loadMore: () => void
  /** True while newer pages remain to load (scroll down) — only ever the case
   *  for an anchored/date-jumped thread, which doesn't start at the tail. */
  hasNewer: boolean
  /** True while a newer page is being fetched. */
  isFetchingNewer: boolean
  /** Fetch the previous (newer) page. */
  loadNewer: () => void
}

/** Build the items query params from the open target (id- or number-scoped). */
function paramsFor(
  target: ConversationTarget | null,
): ListConversationItemsApiV1ConversationItemsGetParams | undefined {
  // Optional agent scope — only the number-opened thread (the embedded chat tab)
  // passes it; URL-opened threads leave it undefined (unfiltered).
  if (target?.conversationId) {
    return { conversation_id: target.conversationId }
  }
  if (target?.phone) {
    return { phone: target.phone, user_id: target.userId }
  }
  return undefined
}

/**
 * Page cursor for the timeline. A date jump rides `to_date` on the FIRST page
 * only — so it lands on that date — then older pages walk the bare `next_cursor`
 * (unfiltered), which is how older history keeps loading as the user scrolls up.
 * Sending `to_date` on every page would cap each older request at the date and
 * the server returns no further pages, breaking scroll-up.
 *
 * A search hit rides `around` on the first page the same way: the server returns
 * a window *centred* on that item — roughly half a page of newer items above it,
 * the rest older — with both cursors set, so the thread pages `older` down and
 * `newer` (`cursor_dir: 'newer'`) back up toward the tail from there.
 */
interface ItemPageParam {
  toDate?: string
  around?: string
  cursor?: string
  /** Which way `cursor` walks; ignored by the server when `around` is set. */
  dir?: ListConversationItemsApiV1ConversationItemsGetCursorDir
}

/** Resolve the counterparty from the open target, falling back to the thread. */
function resolveName(
  target: ConversationTarget | null,
  items: ReadonlyArray<ConversationItemSchema>,
): string {
  if (target?.name) return target.name
  if (items.length === 0) return ''
  const first = items[0]
  return counterpartyTitle(first.customer, first.counterparty_number)
}

function resolveNumber(
  target: ConversationTarget | null,
  items: ReadonlyArray<ConversationItemSchema>,
): string {
  if (target?.phone) return target.phone
  if (items.length === 0) return ''
  return items[0].counterparty_number ?? ''
}

/**
 * The counterparty's CRM customer, carrying inline SMS deliverability. The
 * header is authoritative once loaded; a number-opened thread (no conversation
 * id) resolves from its items instead. `undefined` = still loading (hold the
 * deliverability UI), `null` = confirmed customer-less (number-health
 * fallback).
 */
function resolveCustomer(
  header: ConversationHeader | undefined,
  items: ReadonlyArray<ConversationItemSchema>,
  pending: boolean,
): CustomerSummary | null | undefined {
  if (header) return header.customer ?? null
  for (const item of items) {
    if (item.customer) return item.customer
  }
  return pending ? undefined : null
}

interface ResolvedDetails {
  name: string
  toNumber: string
  customerId?: string
  customer: CustomerSummary | null | undefined
  optedOut: boolean
  blocked: boolean
  starred: boolean
}

/**
 * Counterparty + per-user flags for the header. The thread-detail endpoint
 * (`GET /conversations/{id}`) is authoritative once loaded; until then — and for
 * number-opened threads that carry no conversation id — we fall back to the
 * values riding on the thread's own items.
 */
function resolveDetails(
  header: ConversationHeader | undefined,
  target: ConversationTarget | null,
  items: ReadonlyArray<ConversationItemSchema>,
  pending: boolean,
): ResolvedDetails {
  const customer = resolveCustomer(header, items, pending)
  if (header) {
    return {
      name: counterpartyTitle(header.customer, header.to_number),
      toNumber: header.to_number,
      customerId: header.customer?.id,
      customer,
      optedOut: header.is_opted_out ?? false,
      blocked: header.is_blocked ?? false,
      starred: header.is_favorite ?? false,
    }
  }
  return {
    name: resolveName(target, items),
    toNumber: resolveNumber(target, items),
    customerId: customer?.id,
    customer,
    // Opt-out state only rides on the header — a number-opened thread can't
    // know it, so the send itself stays the backstop (rejected with OPTED_OUT).
    optedOut: false,
    blocked: items.some((item) => item.is_blocked),
    starred: items.some((item) => item.is_favorite),
  }
}

/** "Still loading" for customer resolution: the items query hasn't answered,
 *  or an id-scoped thread's header (the authoritative source) hasn't landed. */
function customerPending(
  itemsLoading: boolean,
  conversationId: string | undefined,
  headerLoading: boolean,
): boolean {
  return itemsLoading || (!!conversationId && headerLoading)
}

/** Map the header endpoint's mainline (when the thread is a shared-line thread)
 *  to the display shape the composer "From" row renders and sends from. */
function toThreadMainline(
  mainline: ConversationMainline | null | undefined,
): ThreadMainline | undefined {
  if (!mainline) return undefined
  const phone = formatPhone(mainline.phone_number)
  const label = mainline.label ?? ''
  return {
    id: mainline.id,
    phone,
    label,
    display: label ? `${phone} · ${label}` : phone,
  }
}

/** Flatten loaded pages into a unique item list (defensive dedupe by id). */
function dedupeItems(
  pages: ReadonlyArray<{ data: { items?: ConversationItemSchema[] } }>,
): ConversationItemSchema[] {
  const byId = new Map<string, ConversationItemSchema>()
  for (const page of pages) {
    for (const item of page.data.items ?? []) byId.set(item.id, item)
  }
  return [...byId.values()]
}

/** Marks a thread read once per open, clearing its unread count + tab badges. */
function useMarkReadOnOpen(target: ConversationTarget | null): void {
  const markRead = useMarkConversationRead()
  const threadKey = target?.conversationId ?? target?.phone
  const markedKey = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!threadKey || !target || markedKey.current === threadKey) return
    markedKey.current = threadKey
    markRead(target)
  }, [threadKey, target, markRead])
}

/**
 * The thread's paged timeline. `to_date` / `around` are part of the query key so
 * a jump reseeds, but they're applied only to the FIRST page (via the page
 * param) — the pages either side of it walk the bare cursors, older going down
 * and newer going up.
 */
function useThreadItems(
  params: ListConversationItemsApiV1ConversationItemsGetParams | undefined,
  active: boolean,
  jumpDate: string | undefined,
  anchorId: string | undefined,
) {
  const queryKey = [
    ...getListConversationItemsApiV1ConversationItemsGetQueryKey({
      ...params,
      to_date: jumpDate,
      around: anchorId,
    }),
    'infinite',
  ]
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      listConversationItemsApiV1ConversationItemsGet(
        {
          ...params,
          to_date: pageParam.toDate,
          around: pageParam.around,
          cursor: pageParam.cursor,
          cursor_dir: pageParam.dir,
          size: PAGE_SIZE,
        },
        signal,
      ),
    initialPageParam: { toDate: jumpDate, around: anchorId } as ItemPageParam,
    getNextPageParam: (last) =>
      last.data.next_cursor
        ? ({
            cursor: last.data.next_cursor,
            dir: ListConversationItemsApiV1ConversationItemsGetCursorDir.older,
          } as ItemPageParam)
        : undefined,
    // "Previous" here means *newer*: only an anchored (or date-jumped) thread
    // starts away from the tail, so this is undefined for a normal open.
    getPreviousPageParam: (first) =>
      first.data.prev_cursor
        ? ({
            cursor: first.data.prev_cursor,
            dir: ListConversationItemsApiV1ConversationItemsGetCursorDir.newer,
          } as ItemPageParam)
        : undefined,
    retry: (failureCount, error) => !isInvalidCursor(error) && failureCount < 3,
    enabled: !!params && active,
    // Always refetch conversation items when a thread is (re)opened — the 5-min
    // global staleTime would otherwise serve a stale cache on reopen.
    staleTime: 0,
    refetchOnMount: 'always',
  })
  useCursorRecovery(query, queryKey)
  // A search-hit anchor or a date jump opens the thread mid-history; anything
  // else opens on the newest page, where there is nothing newer to fetch.
  const pinnedAwayFromTail = anchorId !== undefined || jumpDate !== undefined
  return { query, pinnedAwayFromTail }
}

/** Loads a conversation's timeline (paged) and counterparty details. The target
 *  is passed in so the thread can be driven by the URL (phone panel) or by an
 *  explicit number (embedded, e.g. the shipment sidebar chat tab). A target
 *  carrying `messageId` (a search hit) opens *around* that item instead of at
 *  the tail, and can then page both ways. `active` gates
 *  the (heavy) timeline + header fetches and mark-read: the phone panel passes
 *  its open state so a URL-retained chat target doesn't keep fetching while the
 *  panel is closed; embedded callers leave it at the default. */
export function useConversationThread(
  target: ConversationTarget | null,
  active = true,
  jumpDate?: string,
): ConversationThread {
  const { t } = useTranslation()
  const params = paramsFor(target)
  const { query, pinnedAwayFromTail } = useThreadItems(
    params,
    active,
    jumpDate,
    target?.messageId,
  )

  // Thread header (owner / shared-mainline) — only resolvable for an id-scoped
  // thread; number-opened threads (calls tab, favorites) have no conversation id.
  const conversationId = target?.conversationId
  const headerQuery = useGetConversationApiV1ConversationsConversationIdGet(
    conversationId ?? '',
    { query: { enabled: !!conversationId && active } },
  )
  const header = headerQuery.data?.data
  const mainline = useMemo(() => toThreadMainline(header?.mainline), [header])

  useMarkReadOnOpen(active ? target : null)

  const items = useMemo(
    () => dedupeItems(query.data?.pages ?? []),
    [query.data],
  )
  // Personal-line threads carry their owning agent on the header, not on every
  // item — pass it down so call entries can still show who made/took the call.
  const agentName = userName(header?.user)
  const entries = useMemo(
    () => itemsToEntries(items, t, agentName),
    [items, t, agentName],
  )
  const details = resolveDetails(
    header,
    target,
    items,
    customerPending(query.isLoading, conversationId, headerQuery.isLoading),
  )

  return {
    name: details.name,
    phone: formatPhone(details.toNumber),
    toNumber: details.toNumber,
    customerId: details.customerId,
    customer: details.customer,
    optedOut: details.optedOut,
    mainline,
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    blocked: details.blocked,
    starred: details.starred,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    // Only a thread that opened away from its tail can page newer. Gated on
    // that rather than on `prev_cursor` alone, so a server that always hands
    // back a prev cursor can't make a normally-opened thread chase pages that
    // are already loaded.
    hasNewer: pinnedAwayFromTail && query.hasPreviousPage,
    isFetchingNewer: query.isFetchingPreviousPage,
    loadNewer: () => void query.fetchPreviousPage(),
  }
}
